import { colorCss } from '../lib/colorCss';
import { getCustomIcon, useCustomIcons } from '../lib/customIcons';
/**
 * BuiltinIcon — renders one of a small set of platform-provided icons
 * as an inline SVG. Used by button elements (and potentially other
 * element types in the future) to embed a stock UI symbol next to
 * the label.
 *
 * Why inline SVG instead of the Material Icons font?
 *   - No external font fetch needed (works offline, no FOIT)
 *   - No global CSS class (.material-icons) polluting the page
 *   - Predictable sizing via the `size` prop
 *   - Same visual as Material Icons (paths sourced from the Material
 *     Symbols / Material Icons set)
 *
 * Adding a new icon = adding an entry to the ICONS map below.
 * Each entry is a path string for a 24×24 viewBox (Material Symbols
 * default). The viewBox is `0 -960 960 960` for newer Material
 * Symbols paths.
 *
 * Created in 0.36.79 for the button "External link" indicator.
 */

// SVG path definitions. Each path is drawn inside the viewBox
// `0 -960 960 960` (the standard Material Symbols viewport).
const ICONS = {
  // open_in_new — arrow pointing diagonally out of a box. Universally
  // recognized symbol for "opens in a new tab / window / external
  // destination". Use next to buttons that navigate to external URLs.
  open_in_new:
    'M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h280v80H200v560h560v-280h80v280q0 33-23.5 56.5T760-120H200Zm188-212-56-56 372-372H560v-80h280v280h-80v-144L388-332Z',
  // 0.39.13 - arrow_outward — a plain diagonal "out" arrow, a lighter
  // external-link indicator than open_in_new. These two external-link
  // icons are the only ones kept; the earlier speculative set
  // (link / verified / shield / check_circle / description) was
  // removed as unnecessary for the footer link picker.
  // 0.39.45 - Library/book icon for the library-value picker button.
  // A stack of books - the default system icon for "pick from a
  // library". Rendered in Henaket blue at the call site.
  /* 0.41.87 - A table with its FIRST COLUMN filled: "take this column".
     Material Symbols "table" outline with the left column solid, so it
     reads as a column selected rather than a table inserted. Same 960
     viewBox and d-string form as every other icon here - 0.41.03 broke
     when these were written as JSX elements. */
  table_column: 'M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h160v-560H200v560Zm240 0h320v-240H440v240Zm0-320h320v-240H440v240Z',
  // 0.44.36 - Material Symbols palette: the color picker's button.
  palette:
    'M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 32.5-156t88-127Q256-817 330-848.5T488-880q80 0 151 27.5t124.5 76q53.5 48.5 85 115T880-518q0 115-70 176.5T640-280h-74q-9 0-12.5 5t-3.5 11q0 12 15 34.5t15 51.5q0 50-27.5 74T480-80Zm0-400Zm-220 40q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17Zm120-160q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17Zm200 0q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17Zm120 160q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17ZM480-160q9 0 14.5-5t5.5-13q0-14-15-33t-15-57q0-42 29-67t71-25h70q66 0 113-38.5T800-518q0-121-92.5-201.5T488-800q-136 0-232 93t-96 227q0 133 93.5 226.5T480-160Z',
  library:
    'M300-360q17 0 28.5-11.5T340-400v-360q0-17-11.5-28.5T300-800q-17 0-28.5 11.5T260-760v360q0 17 11.5 28.5T300-360Zm260-340h100q17 0 28.5-11.5T700-740q0-17-11.5-28.5T660-780H560q-17 0-28.5 11.5T520-740q0 17 11.5 28.5T560-700Zm0 160h100q17 0 28.5-11.5T700-580q0-17-11.5-28.5T660-620H560q-17 0-28.5 11.5T520-580q0 17 11.5 28.5T560-540ZM260-160h440q17 0 28.5-11.5T740-200v-40H260q-17 0-28.5 11.5T220-200q0 17 11.5 28.5T260-160Zm-60 160q-33 0-56.5-23.5T120-80v-680q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200v40q0 17-11.5 28.5T160 0q-17 0-28.5-11.5T120-40v-40q0-33 23.5-56.5T200-160h-40Z',
  // 0.39.49 - content_copy: two overlapping sheets, the standard
  // "copy to clipboard" glyph.
  content_copy:
    'M360-240q-33 0-56.5-23.5T280-320v-480q0-33 23.5-56.5T360-880h360q33 0 56.5 23.5T800-800v480q0 33-23.5 56.5T720-240H360Zm0-80h360v-480H360v480ZM200-80q-33 0-56.5-23.5T120-160v-520q0-17 11.5-28.5T160-720q17 0 28.5 11.5T200-680v520h400q17 0 28.5 11.5T640-120q0 17-11.5 28.5T600-80H200Zm160-240v-480 480Z',
  // 0.40.16 - Up / down arrows for reordering list items (table
  // column order today). Material Symbols arrow_upward /
  // arrow_downward.
  arrow_upward:
    'M440-240v-448L236-484l-56-56 300-300 300 300-56 56-204-204v448h-80Z',
  arrow_downward:
    'M440-720v448L236-476l-56 56 300 300 300-300-56-56-204 204v-448h-80Z',
  // 0.41.03 - Material Symbols visibility / visibility_off, for the
  // password reveal toggle. These MUST be `d` attribute strings in the
  // 0 -960 960 960 viewBox like every other entry: the component does
  // `<path d={path} />`, so a JSX element here renders nothing (0.40.98
  // added them as <path> elements with 24-unit coordinates, which is
  // why the button appeared empty).
  visibility:
    'M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320Zm0-72q-45 0-76.5-31.5T372-500q0-45 31.5-76.5T480-608q45 0 76.5 31.5T588-500q0 45-31.5 76.5T480-392Zm0 192q-146 0-266-81.5T40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200Zm0-300Zm0 220q113 0 207.5-59.5T832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280Z',
  visibility_off:
    'm644-428-58-58q9-47-27-88t-93-32l-58-58q17-8 34.5-12t37.5-4q75 0 127.5 52.5T660-500q0 20-4 37.5T644-428Zm128 126-58-56q38-29 67.5-63.5T832-500q-50-101-143.5-160.5T480-720q-29 0-57 4t-55 12l-62-62q41-17 84.5-25.5T480-800q146 0 265.5 81.5T920-500q-26 64-67 117t-81 81Zm20 246L624-222q-35 11-70.5 16.5T480-200q-146 0-265.5-81.5T40-500q22-53 61-99.5T145-679L27-798l57-57 764 764-56 57ZM222-624q-29 26-53 57t-41 67q50 101 143.5 160.5T480-280q20 0 39-2.5t39-5.5l-36-38q-11 3-21 4.5t-21 1.5q-75 0-127.5-52.5T300-500q0-11 1.5-21t4.5-21l-84-82Zm319 93Zm-151 75Z',
  arrow_outward:
    'M256-240l-56-56 384-384H240v-80h480v480h-80v-344L256-240Z',
};

// Human-readable labels for each icon. Used by the admin config UI
// (dropdown options). Keep these short and descriptive.
/* 0.43.78 - Every icon the platform draws, for the Button's icon
 * dropdown (and any other picker). The outlined ones were private to
 * the sidebar, the Settings cards, the user menu, the table row actions
 * and the sort arrows; they are copied here as drawn there (24- or
 * 16-unit viewBox, stroke in currentColor) so one registry holds them
 * all. Static markup, never user input. */
const OUTLINE_ICONS = {
  audit: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z\" /> <polyline points=\"14 2 14 8 20 8\" /> <line x1=\"9\" y1=\"13\" x2=\"15\" y2=\"13\" /> <line x1=\"9\" y1=\"17\" x2=\"15\" y2=\"17\" /> <line x1=\"9\" y1=\"9\" x2=\"11\" y2=\"9\" />" },
  brand: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<circle cx=\"13.5\" cy=\"6.5\" r=\"1.5\" /> <circle cx=\"17.5\" cy=\"10.5\" r=\"1.5\" /> <circle cx=\"8.5\" cy=\"7.5\" r=\"1.5\" /> <circle cx=\"6.5\" cy=\"12.5\" r=\"1.5\" /> <path d=\"M12 22a10 10 0 0 1 0-20c5 0 10 4 10 9 0 4-3 5-5 5h-2c-1 0-2 1-2 2 0 1 1 2 1 3 0 1-1 1-2 1z\" />" },
  building: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M3 21h18\" /> <path d=\"M5 21V9l7-5 7 5v12\" /> <path d=\"M9 21v-6M15 21v-6M12 21v-3\" /> <path d=\"M5 9h14\" />" },
  // 0.47.19 - The payment card's two icons (Material Symbols payments and
  // credit_card, redrawn from the Henaket payment-card spec): 32-unit
  // viewBox, 3px stroke. In the library so any element can use them.
  cash: { viewBox: "0 0 32 32", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "3"}, inner: "<rect x=\"2.5\" y=\"6.5\" width=\"21.5\" height=\"13.5\" rx=\"1.5\" /> <circle cx=\"13.5\" cy=\"13.25\" r=\"4.25\" fill=\"currentColor\" stroke=\"none\" /> <path d=\"M29 9v14.5a1.5 1.5 0 0 1-1.5 1.5H5\" />" },
  payment_card: { viewBox: "0 0 32 32", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "3"}, inner: "<rect x=\"4\" y=\"6.5\" width=\"24\" height=\"19\" rx=\"1.5\" /> <rect x=\"5.5\" y=\"10.5\" width=\"21\" height=\"5.5\" fill=\"currentColor\" stroke=\"none\" />" },
  clock: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<circle cx=\"12\" cy=\"12\" r=\"9\" /> <polyline points=\"12 7 12 12 15 14\" />" },
  docs: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M2 4 h7 a3 3 0 0 1 3 3 v14 a2 2 0 0 0 -2 -2 H2 z\" /> <path d=\"M22 4 h-7 a3 3 0 0 0 -3 3 v14 a2 2 0 0 1 2 -2 h8 z\" />" },
  // 0.48.24 - Courses: the presenter's teacher at a board with an audience
  // (offline), on a laptop screen (online), and a room with one learner
  // joining on a laptop (hybrid). Solid, wide (60 by 48).
  // 0.48.42 - The board turned at the online laptop's angle (about
  // 30 degrees), the teacher and the audience upright.
  course_offline: { viewBox: "0 0 60 48", attrs: {"fill": "currentColor"}, inner: "<g transform=\"matrix(0.78 0.18 0 0.86 6 -1.5)\"><rect x=\"22\" y=\"2\" width=\"35\" height=\"18\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.5\" /></g><g transform=\"translate(1 1) scale(1.0)\"><circle cx=\"9\" cy=\"5.6\" r=\"4\" /><path fill-rule=\"evenodd\" d=\"M7.2 11 L9 19.5 L10.8 11 H12.6 a4 4 0 0 1 4 4 V24 H1.4 V15 a4 4 0 0 1 4-4 Z\" /><path d=\"M8.3 11.4 H9.7 L9.45 12.6 H8.55 Z M8.55 13.1 H9.45 L9.75 15.6 L9 17.2 L8.25 15.6 Z\" /><path d=\"M14.6 13.6 L21.5 9\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"3\" stroke-linecap=\"round\" /></g><circle cx=\"23\" cy=\"27.4\" r=\"3.3\" /><circle cx=\"37\" cy=\"27.4\" r=\"3.3\" /><circle cx=\"16\" cy=\"32.6\" r=\"3.8\" /><path d=\"M9.8 47.5 V43.18 A6.2 5.58 0 0 1 22.2 43.18 V47.5 Z\" /><circle cx=\"30\" cy=\"32.6\" r=\"3.8\" /><path d=\"M23.8 47.5 V43.18 A6.2 5.58 0 0 1 36.2 43.18 V47.5 Z\" /><circle cx=\"44\" cy=\"32.6\" r=\"3.8\" /><path d=\"M37.8 47.5 V43.18 A6.2 5.58 0 0 1 50.2 43.18 V47.5 Z\" />" },
  // 0.48.41 - The laptop turned about 30 degrees, in perspective: a
  // slanted screen and a keyboard deck, so it reads as a laptop.
  course_online: { viewBox: "0 0 60 48", attrs: {"fill": "currentColor"}, inner: "<g transform=\"matrix(0.78 0.18 0 0.86 12 0)\"><rect x=\"7\" y=\"2\" width=\"46\" height=\"33\" rx=\"2\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.6\" /><rect x=\"28\" y=\"7\" width=\"20\" height=\"12\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\" /><g transform=\"translate(10 9.6) scale(0.92)\"><circle cx=\"9\" cy=\"5.6\" r=\"4\" /><path fill-rule=\"evenodd\" d=\"M7.2 11 L9 19.5 L10.8 11 H12.6 a4 4 0 0 1 4 4 V24 H1.4 V15 a4 4 0 0 1 4-4 Z\" /><path d=\"M8.3 11.4 H9.7 L9.45 12.6 H8.55 Z M8.55 13.1 H9.45 L9.75 15.6 L9 17.2 L8.25 15.6 Z\" /><path d=\"M14.6 13.6 L21.5 9\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"3\" stroke-linecap=\"round\" /></g></g><path d=\"M17.46 31.36 L53.34 39.64 L39.34 45.64 L3.46 37.36 Z\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linejoin=\"round\" /><path d=\"M3.46 37.36 L39.34 45.64 L39.34 47.44 L3.46 39.16 Z\" /><path d=\"M18.69 33.67 L45.24 39.80\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.2\" stroke-dasharray=\"2.4 1.1\" /><path d=\"M15.89 34.87 L42.44 41.00\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.2\" stroke-dasharray=\"2.4 1.1\" /><path d=\"M13.09 36.07 L39.64 42.20\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.2\" stroke-dasharray=\"2.4 1.1\" /><path d=\"M21.25 39.43 L27.71 40.93 L25.89 41.71 L19.43 40.21 Z\" />" },
  // 0.48.43 - The board turned as in course_offline, the small laptop
  // turned as in course_online (with a remote participant on its screen).
  course_hybrid: { viewBox: "0 0 60 48", attrs: {"fill": "currentColor"}, inner: "<g transform=\"matrix(0.78 0.18 0 0.86 6 -1.5)\"><rect x=\"22\" y=\"2\" width=\"35\" height=\"18\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.5\" /></g><g transform=\"translate(1 1) scale(1.0)\"><circle cx=\"9\" cy=\"5.6\" r=\"4\" /><path fill-rule=\"evenodd\" d=\"M7.2 11 L9 19.5 L10.8 11 H12.6 a4 4 0 0 1 4 4 V24 H1.4 V15 a4 4 0 0 1 4-4 Z\" /><path d=\"M8.3 11.4 H9.7 L9.45 12.6 H8.55 Z M8.55 13.1 H9.45 L9.75 15.6 L9 17.2 L8.25 15.6 Z\" /><path d=\"M14.6 13.6 L21.5 9\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"3\" stroke-linecap=\"round\" /></g><circle cx=\"23\" cy=\"27.4\" r=\"3.3\" /><circle cx=\"16\" cy=\"32.6\" r=\"3.8\" /><path d=\"M9.8 47.5 V43.18 A6.2 5.58 0 0 1 22.2 43.18 V47.5 Z\" /><circle cx=\"30\" cy=\"32.6\" r=\"3.8\" /><path d=\"M23.8 47.5 V43.18 A6.2 5.58 0 0 1 36.2 43.18 V47.5 Z\" /><g transform=\"matrix(0.78 0.18 0 0.86 41 26.5)\"><rect x=\"0\" y=\"0\" width=\"19\" height=\"13\" rx=\"1\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" /><circle cx=\"9.5\" cy=\"4.6\" r=\"2.3\" /><path d=\"M5.3 11.6 V10.2 a4.2 3.4 0 0 1 8.4 0 V11.6 Z\" /></g><path d=\"M41.00 37.68 L55.82 41.10 L49.82 43.70 L35.00 40.28 Z\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\" stroke-linejoin=\"round\" /><path d=\"M35.00 40.28 L49.82 43.70 L49.82 45.00 L35.00 41.58 Z\" /><path d=\"M40.97 39.07 L51.65 41.53\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.5\" stroke-dasharray=\"1.8 0.9\" /><path d=\"M39.17 39.85 L49.85 42.31\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.5\" stroke-dasharray=\"1.8 0.9\" />" },
  // 0.48.10 - Docs and references: a page of text with a folded
  // corner and a second page behind it. Solid, like the presenter.
  // 0.48.11 - Wider (60 by 48): the back page shown whole.
  docs_references: { viewBox: "0 0 60 48", attrs: {"fill": "currentColor"}, inner: "<path fill-rule=\"evenodd\" d=\"M5 2 H22 L29 9 V12.4 H22.4 V40 H5 Z M21 4.6 V10 H26.4 Z M9 13 H18.4 V15.4 H9 Z M9 19 H18.4 V21.4 H9 Z M9 25 H18.4 V27.4 H9 Z M9 31 H15 V33.4 H9 Z\" /> <path fill-rule=\"evenodd\" d=\"M25 14.4 H43 L52 23.4 V46 H25 Z M42 17 V24.4 H49.4 Z M29.5 28 H47.5 V30.4 H29.5 Z M29.5 34 H47.5 V36.4 H29.5 Z M29.5 40 H40 V42.4 H29.5 Z\" />" },
  document: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "STROKE", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M14 3 H 7 a 2 2 0 0 0 -2 2 v 14 a 2 2 0 0 0 2 2 h 10 a 2 2 0 0 0 2 -2 V 8 Z\" /> <polyline points=\"14 3 14 8 19 8\" />" },
  dropdown: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<rect x=\"3\" y=\"6\" width=\"18\" height=\"12\" rx=\"2\" /> <polyline points=\"9 11 12 14 15 11\" />" },
  form: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<rect x=\"4\" y=\"3\" width=\"16\" height=\"18\" rx=\"2\" /> <line x1=\"8\" y1=\"8\" x2=\"16\" y2=\"8\" /> <line x1=\"8\" y1=\"12\" x2=\"16\" y2=\"12\" /> <line x1=\"8\" y1=\"16\" x2=\"13\" y2=\"16\" />" },
  globe: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<circle cx=\"12\" cy=\"12\" r=\"10\" /> <ellipse cx=\"12\" cy=\"12\" rx=\"4\" ry=\"10\" /> <line x1=\"2\" y1=\"12\" x2=\"22\" y2=\"12\" />" },
  globe_lang: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<circle cx=\"12\" cy=\"12\" r=\"9\" /> <path d=\"M3 12h18 M12 3a14 14 0 0 1 0 18 M12 3a14 14 0 0 0 0 18\" />" },
  globe_map: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<circle cx=\"12\" cy=\"12\" r=\"9\" /> <path d=\"M12 7c-2 1.5-3.5 3-3.5 5s1.5 3.5 3.5 5c2-1.5 3.5-3 3.5-5S14 8.5 12 7z\" /> <circle cx=\"12\" cy=\"12\" r=\"1.25\" fill=\"currentColor\" />" },
  helpdesk: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<circle cx=\"12\" cy=\"12\" r=\"9\" /> <path d=\"M4.93 4.93 L8.46 8.46\" /> <path d=\"M15.54 15.54 L19.07 19.07\" /> <path d=\"M4.93 19.07 L8.46 15.54\" /> <path d=\"M15.54 8.46 L19.07 4.93\" /> <circle cx=\"12\" cy=\"12\" r=\"3.5\" />" },
  home: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M3 12 L12 3 L21 12\" /> <path d=\"M5 10 V21 H19 V10\" />" },
  id_card: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<rect x=\"2.5\" y=\"5\" width=\"19\" height=\"14\" rx=\"2\" /> <circle cx=\"8.5\" cy=\"11\" r=\"2\" /> <path d=\"M5.5 16c.6-1.2 1.7-1.9 3-1.9s2.4.7 3 1.9\" /> <path d=\"M14.5 10h4M14.5 13.5h4\" />" },
  libraries: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<rect x=\"3\" y=\"4\" width=\"4\" height=\"16\" rx=\"1\" /> <rect x=\"9\" y=\"4\" width=\"4\" height=\"16\" rx=\"1\" /> <path d=\"M16 4.5 L20.5 5.5 L18 20 L13.5 19\" /> <path d=\"M3 20 L21 20\" />" },
  location: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z\" /> <circle cx=\"12\" cy=\"10\" r=\"3\" />" },
  // 0.46.79 - Log in: the arrow goes into the door (logout's mirror).
  login: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "2", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M15 3 h 4 a 2 2 0 0 1 2 2 V 19 a 2 2 0 0 1 -2 2 h -4\" /> <polyline points=\"10 17 15 12 10 7\" /> <line x1=\"15\" y1=\"12\" x2=\"3\" y2=\"12\" />" },
  logout: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "2", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M15 21 h 4 a 2 2 0 0 0 2 -2 V 5 a 2 2 0 0 0 -2 -2 h -4\" /> <polyline points=\"8 17 3 12 8 7\" /> <line x1=\"3\" y1=\"12\" x2=\"15\" y2=\"12\" />" },
  mail: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<rect x=\"3\" y=\"5\" width=\"18\" height=\"14\" rx=\"2\" /> <polyline points=\"3 7 12 13 21 7\" />" },
  pencil: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "ROW_ACTION_ICON_STROKE", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M12 20h9\" /> <path d=\"M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z\" />" },
  person: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "STROKE", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<circle cx=\"12\" cy=\"8\" r=\"4\" /> <path d=\"M4 21c0-4 4-7 8-7s8 3 8 7\" />" },
  phone_plus: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z\" /> <path d=\"M16 3h6M19 0v6\" stroke-width=\"1.5\" />" },
  plug: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M9 2v6M15 2v6M5 8h14v3a7 7 0 0 1-14 0z M12 18v4\" />" },
  // 0.48.01 - A teacher in a suit and tie. 0.48.03 - Shown from the
  // waist up, holding a book (cut out of the jacket) and pointing at a
  // whiteboard with writing. 0.48.04 - Wider (60 by 48), the board
  // lower and wider. 0.48.08 - Taller, and wider to the left, where it
  // passes behind the teacher. Solid (not outline), for the advocates
  // register.
  presenter: { viewBox: "0 0 60 48", attrs: {"fill": "currentColor"}, inner: "<path d=\"M18.6 7 H58 V34.5 H25\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" /> <path d=\"M34 14 H53 M34 19 H49.5 M34 24 H43\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\" stroke-linecap=\"round\" /> <circle cx=\"12\" cy=\"11.5\" r=\"5.6\" /> <path fill-rule=\"evenodd\" d=\"M8.6 19.5 L12 33 L15.4 19.5 H17.5 a5.5 5.5 0 0 1 5.5 5.5 V48 H1 V25 a5.5 5.5 0 0 1 5.5-5.5 Z M1.32 34.58 L11.10 32.50 L13.68 44.62 L3.90 46.70 Z\" /> <path d=\"M10.8 20 H13.2 L12.7 22.2 H11.3 Z\" /> <path d=\"M11.3 23 H12.7 L13.1 26.8 L12 29.3 L10.9 26.8 Z\" /> <path fill-rule=\"evenodd\" d=\"M2.39 35.27 L10.41 33.56 L12.61 43.93 L4.59 45.64 Z M3.76 34.98 L4.44 34.83 L6.65 45.20 L5.96 45.35 Z M6.33 43.73 L12.30 42.46 L12.44 43.10 L6.47 44.37 Z\" /> <path d=\"M21 25 L28.5 23\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"4.4\" stroke-linecap=\"round\" /> <path d=\"M28.5 23 L32.6 19.8\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\" stroke-linecap=\"round\" />" },
  settings: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<circle cx=\"12\" cy=\"12\" r=\"3\" /> <path d=\"M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z\" />" },
  shield: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M12 3 L4 6 V12 C4 17 8 20.5 12 21 C16 20.5 20 17 20 12 V6 Z\" />" },
  sort: { viewBox: "0 0 16 16", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "0.6", "stroke-linejoin": "miter"}, inner: "<path d=\"M 8 1 L 14 7 L 2 7 Z\" /> <path d=\"M 2 9 L 14 9 L 8 15 Z\" />" },
  sort_ascending: { viewBox: "0 0 16 16", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "0.6", "stroke-linejoin": "miter"}, inner: "<path d=\"M 8 1 L 14 7 L 2 7 Z\" fill=\"currentColor\" /> <path d=\"M 2 9 L 14 9 L 8 15 Z\" />" },
  sort_descending: { viewBox: "0 0 16 16", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "0.6", "stroke-linejoin": "miter"}, inner: "<path d=\"M 8 1 L 14 7 L 2 7 Z\" /> <path d=\"M 2 9 L 14 9 L 8 15 Z\" fill=\"currentColor\" />" },
  spark: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M10 2l2.1 5.9L18 10l-5.9 2.1L10 18l-2.1-5.9L2 10l5.9-2.1z\" /><path d=\"M18.5 14.5l1.1 2.9 2.9 1.1-2.9 1.1-1.1 2.9-1.1-2.9-2.9-1.1 2.9-1.1z\" /><path d=\"M19 2l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z\" />" },
  stamp: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M5 21h14\" /> <path d=\"M7 17v-2a2 2 0 0 1 1-1.7l2-1c.6-.3 1-1 1-1.7V8.5a2.5 2.5 0 1 1 5 0V11c0 .7.4 1.4 1 1.7l-1 0a2 2 0 0 1 1 1.7v2H7z\" />" },
  text: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M4 7 V5 H20 V7\" /> <line x1=\"12\" y1=\"5\" x2=\"12\" y2=\"19\" /> <line x1=\"9\" y1=\"19\" x2=\"15\" y2=\"19\" />" },
  trash: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "ROW_ACTION_ICON_STROKE", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<polyline points=\"3 6 5 6 21 6\" /> <path d=\"M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6\" /> <path d=\"M10 11v6\" /> <path d=\"M14 11v6\" /> <path d=\"M9 6V4a2 2 0 012-2h2a2 2 0 012 2v2\" />" },
  // 0.48.12 - Notification: the urgent bell without its badge, centred
  // between ringing lines on both sides. Solid, wide (60 by 48).
  notification: { viewBox: "0 0 60 48", attrs: {"fill": "currentColor"}, inner: "<g transform=\"translate(4 0)\"> <path d=\"M26 5 a2.6 2.6 0 0 1 2.6 2.6 V8.4 C34.5 9.8 38 14.8 38 21 V30 L42 35 V37.4 H10 V35 L14 30 V21 C14 14.8 17.5 9.8 23.4 8.4 V7.6 A2.6 2.6 0 0 1 26 5 Z\" /> <path d=\"M21.5 39.6 H30.5 a4.5 4.5 0 0 1 -9 0 Z\" /> </g> <g fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.6\" stroke-linecap=\"round\"> <path d=\"M10.5 15 C7.5 19.5 7.5 26.5 10.5 31\" /> <path d=\"M5.6 11.5 C2.4 18 2.4 28 5.6 34.5\" /> <path d=\"M49.5 15 C52.5 19.5 52.5 26.5 49.5 31\" /> <path d=\"M54.4 11.5 C57.6 18 57.6 28 54.4 34.5\" /> </g>" },
  // 0.48.11 - Urgent notification: a ringing bell with an exclamation
  // badge. Solid, wide (60 by 48).
  urgent_notification: { viewBox: "0 0 60 48", attrs: {"fill": "currentColor"}, inner: "<path d=\"M26 5 a2.6 2.6 0 0 1 2.6 2.6 V8.4 C34.5 9.8 38 14.8 38 21 V30 L42 35 V37.4 H10 V35 L14 30 V21 C14 14.8 17.5 9.8 23.4 8.4 V7.6 A2.6 2.6 0 0 1 26 5 Z\" /> <path d=\"M21.5 39.6 H30.5 a4.5 4.5 0 0 1 -9 0 Z\" /> <path fill-rule=\"evenodd\" d=\"M49 2.5 a9.5 9.5 0 1 1 0 19 a9.5 9.5 0 1 1 0 -19 Z M47.6 6.6 H50.4 V14.4 H47.6 Z M49 15.9 a1.6 1.6 0 1 1 0 3.2 a1.6 1.6 0 1 1 0 -3.2 Z\" /> <g fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.6\" stroke-linecap=\"round\"> <path d=\"M7.5 15 C4.5 19.5 4.5 26.5 7.5 31\" /> <path d=\"M3.8 11.5 C0.9 18 0.9 28 3.8 34.5\" /> <path d=\"M44.5 25 C47 28.5 47 33 44.5 36.5\" /> <path d=\"M49.4 23.8 C52.8 28.4 52.8 33.4 49.4 38\" /> </g>" },
  user_circle: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "STROKE", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<circle cx=\"12\" cy=\"12\" r=\"10\" /> <circle cx=\"12\" cy=\"10\" r=\"3.2\" /> <path d=\"M5.5 18.5 C 7 15.5 9.5 14.2 12 14.2 C 14.5 14.2 17 15.5 18.5 18.5\" />" },
  user_plus: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<circle cx=\"10\" cy=\"8\" r=\"3.5\" /> <path d=\"M3.5 20a6.5 6.5 0 0 1 13 0\" /> <path d=\"M19 11v6 M16 14h6\" />" },
  users: { viewBox: "0 0 24 24", attrs: {"fill": "none", "stroke": "currentColor", "stroke-width": "1.75", "stroke-linecap": "round", "stroke-linejoin": "round"}, inner: "<path d=\"M17 21 v-2 a4 4 0 0 0 -4 -4 H 5 a4 4 0 0 0 -4 4 v2\" /> <circle cx=\"9\" cy=\"7\" r=\"4\" /> <path d=\"M23 21 v-2 a4 4 0 0 0 -3 -3.87\" /> <path d=\"M16 3.13 a4 4 0 0 1 0 7.75\" />" },
};

/* 0.43.81 - Icon colours: Henaket tokens only, stored by token name so a
 * palette change in the design system flows through. '' = the colour of
 * the text around the icon (the button label's). */
export const ICON_COLOR_OPTIONS = [
  { value: '', label: 'Same as the button text' },
  { value: '--color-text-800', label: 'Text, dark (#1a1a1a)' },
  { value: '--color-text-700', label: 'Text, medium (#454545)' },
  { value: '--color-text-600', label: 'Text, light (#747474)' },
  { value: '--color-grey-700', label: 'Grey (#4b4a4a)' },
  { value: '--color-white', label: 'White' },
  { value: '--color-link', label: 'Link blue (#004d99)' },
  { value: '--color-blue-800', label: 'Blue (#355c8c)' },
  { value: '--color-blue-1000', label: 'Blue, dark (#203754)' },
  { value: '--color-govtech-blue', label: 'Govtech blue (#1871a2)' },
  { value: '--color-green-900', label: 'Green (#45928e)' },
  { value: '--color-start-default', label: 'Start green (#167f16)' },
  { value: '--color-success', label: 'Success (#358000)' },
  { value: '--color-info', label: 'Info (#1b86c3)' },
  { value: '--color-warning', label: 'Warning (#febb30)' },
  { value: '--color-error', label: 'Error (#cc0000)' },
  { value: '--color-error-dark', label: 'Error, dark (#971716)' },
  { value: '--color-henaket-accent', label: 'Henaket accent (#ff6a38)' },
  { value: '--color-pink-800', label: 'Pink (#d81884)' },
];
/* The CSS colour for a stored icon colour ('' -> the text colour). */
export function iconColorValue(token) {
  // 0.44.31 - A Henaket token or a custom '#RRGGBB'.
  return colorCss(token) || 'currentColor';
}

export const BUILTIN_ICON_OPTIONS = [
  {
    "value": "arrow_downward",
    "label": "Arrow down"
  },
  {
    "value": "arrow_outward",
    "label": "Arrow outward"
  },
  {
    "value": "arrow_upward",
    "label": "Arrow up"
  },
  {
    "value": "audit",
    "label": "Audit log"
  },
  {
    "value": "brand",
    "label": "Branding"
  },
  {
    "value": "building",
    "label": "Building"
  },
  {
    "value": "cash",
    "label": "Cash"
  },
  {
    "value": "clock",
    "label": "Clock"
  },
  {
    "value": "content_copy",
    "label": "Copy"
  },
  {
    "value": "course_hybrid",
    "label": "Course (hybrid)"
  },
  {
    "value": "course_offline",
    "label": "Course (offline)"
  },
  {
    "value": "course_online",
    "label": "Course (online)"
  },
  {
    "value": "docs_references",
    "label": "Docs and references"
  },
  {
    "value": "document",
    "label": "Document"
  },
  {
    "value": "docs",
    "label": "Documentation"
  },
  {
    "value": "dropdown",
    "label": "Dropdown"
  },
  {
    "value": "open_in_new",
    "label": "External link (arrow in box)"
  },
  {
    "value": "form",
    "label": "Form"
  },
  {
    "value": "globe",
    "label": "Globe"
  },
  {
    "value": "globe_lang",
    "label": "Globe (language)"
  },
  {
    "value": "globe_map",
    "label": "Globe (map)"
  },
  {
    "value": "helpdesk",
    "label": "Helpdesk"
  },
  {
    "value": "visibility_off",
    "label": "Hidden (eye crossed)"
  },
  {
    "value": "home",
    "label": "Home"
  },
  {
    "value": "id_card",
    "label": "ID card"
  },
  {
    "value": "libraries",
    "label": "Libraries"
  },
  {
    "value": "library",
    "label": "Library"
  },
  {
    "value": "location",
    "label": "Location"
  },
  {
    "value": "login",
    "label": "Log in"
  },
  {
    "value": "logout",
    "label": "Log out"
  },
  {
    "value": "mail",
    "label": "Mail"
  },
  {
    "value": "notification",
    "label": "Notification"
  },
  {
    "value": "palette",
    "label": "Palette"
  },
  {
    "value": "payment_card",
    "label": "Payment card"
  },
  {
    "value": "pencil",
    "label": "Pencil (edit)"
  },
  {
    "value": "person",
    "label": "Person"
  },
  {
    "value": "phone_plus",
    "label": "Phone (add)"
  },
  {
    "value": "plug",
    "label": "Plug (connection)"
  },
  {
    "value": "presenter",
    "label": "Presenter (suit and tie)"
  },
  {
    "value": "settings",
    "label": "Settings"
  },
  {
    "value": "shield",
    "label": "Shield"
  },
  {
    "value": "sort",
    "label": "Sort"
  },
  {
    "value": "sort_ascending",
    "label": "Sort ascending"
  },
  {
    "value": "sort_descending",
    "label": "Sort descending"
  },
  {
    "value": "spark",
    "label": "Spark (AI)"
  },
  {
    "value": "stamp",
    "label": "Stamp"
  },
  {
    "value": "table_column",
    "label": "Table column"
  },
  {
    "value": "text",
    "label": "Text"
  },
  {
    "value": "trash",
    "label": "Trash (delete)"
  },
  {
    "value": "urgent_notification",
    "label": "Urgent notification"
  },
  {
    "value": "user_plus",
    "label": "User (add)"
  },
  {
    "value": "user_circle",
    "label": "User (circle)"
  },
  {
    "value": "users",
    "label": "Users"
  },
  {
    "value": "visibility",
    "label": "Visible (eye)"
  }
];

/* 0.44.51 - The built-in icons plus the ones added in Settings > Icons
 * ("custom.<key>"), for every icon dropdown. */
export function useIconOptions() {
  const custom = useCustomIcons();
  return [
    ...BUILTIN_ICON_OPTIONS,
    ...custom.map(i => ({ value: i.name || `custom.${i.key}`, label: `${i.label} (added)` })),
  ];
}

export default function BuiltinIcon({ name, size = 16, color = 'currentColor', title }) {
  // 0.44.51 - An icon added in Settings > Icons. The hook runs for every
  // icon (hooks cannot be conditional) so a custom one draws as soon as
  // the list arrives.
  useCustomIcons();
  // 0.44.53 - "external" (the outline arrow-out icon) was removed; a
  // button saved with it shows the kept External link icon instead.
  if (name === 'external') name = 'open_in_new';
  const custom = typeof name === 'string' && name.startsWith('custom.') ? getCustomIcon(name) : null;
  const path = custom ? null : ICONS[name];
  const outline = custom
    ? { viewBox: custom.view_box, attrs: custom.attrs || {}, inner: custom.inner }
    : (!path ? OUTLINE_ICONS[name] : null);
  if (!path && !outline) return null;
  if (outline) {
    const isLenO = typeof size === 'string';
    const kebabToCamel = (k) => k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    const svgAttrs = {};
    for (const [k, v] of Object.entries(outline.attrs || {})) {
      svgAttrs[kebabToCamel(k)] = v === 'currentColor' ? color : v;
    }
    /* 0.48.04 - A wider-than-tall drawing (the presenter, 60 by 48) is
       drawn wider at the same height, not shrunk into a square. */
    // 0.48.07 - Built-in icons only: an added (uploaded) icon keeps its
    // square box, as before.
    const vb = custom ? [] : String(outline.viewBox || '').trim().split(/\s+/).map(Number);
    const aspect = vb.length === 4 && vb[2] > 0 && vb[3] > 0 ? vb[2] / vb[3] : 1;
    const widthO = aspect === 1 ? size : (isLenO ? `calc(${size} * ${aspect})` : Math.round(size * aspect * 100) / 100);
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={isLenO ? undefined : widthO}
        height={isLenO ? undefined : size}
        style={isLenO ? { width: widthO, height: size, color } : { color }}
        viewBox={outline.viewBox}
        {...svgAttrs}
        role={title ? 'img' : 'presentation'}
        aria-hidden={title ? undefined : 'true'}
        aria-label={title || undefined}
        dangerouslySetInnerHTML={{ __html: outline.inner }}
      />
    );
  }
  // 0.39.15 - `size` may be a number (px) or a CSS length string
  // (e.g. "1em"). A string is applied via width/height style so the
  // icon can scale with the surrounding font size; a number keeps the
  // original width/height SVG attributes. Material Symbols paths have
  // built-in padding inside the 960 viewBox, so the visible glyph
  // reads ~2px smaller than the box - a 16px box looks ~14px, which
  // is why the old size=14 looked ~12px.
  const isLen = typeof size === 'string';
  const dim = isLen ? undefined : size;
  const lenStyle = isLen ? { width: size, height: size } : null;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={dim}
      height={dim}
      viewBox="0 -960 960 960"
      fill={color}
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : 'true'}
      aria-label={title || undefined}
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...(lenStyle || {}) }}
    >
      <path d={path} />
    </svg>
  );
}
